import { format, parseISO } from 'date-fns';
import { useMemo, useState, useEffect } from 'react';
import { useTable, usePagination, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import SimpleDataTable from '../../../../../../doctorAppointmentManager/components/appointmentHistory/components/simpleDataTable/SimpleDataTable';
import { deleteEducation } from '../../api/updateEducation';
import DeleteConfirmationModal from '../../../../../../common/components/deleteConfirmationModal/DeleteConfirmationModal';
import SuccessConfirmation from '../../../../../../common/components/successConfirmation/SuccessConfirmation';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function EducationList({ items, onClick, refreshData }) {
  const [errorMessage, setErrorMessage] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [localItems, setLocalItems] = useState(items);
  
  // Sync localItems with items prop
  useEffect(() => {
    setLocalItems(items);
  }, [items]);
  
  const columns = useMemo(() => {
    return [
      { Header: 'Título', accessor: 'col1' },
      { Header: 'Descripción', accessor: 'col2' },
      { Header: 'Creación', accessor: 'col3' },
      { Header: 'Estado', accessor: 'col4' },
      { Header: '', accessor: 'col5' }
    ];
  }, []);

  const handleDelete = (e, item) => {
    e.preventDefault();
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      const result = await deleteEducation(itemToDelete.id, setErrorMessage);
      
      if (result === undefined || result.status >= 400) {
        setShowDeleteModal(false);
        setItemToDelete(null);
        setTimeout(() => {
          setErrorMessage(null);
        }, 5000);
        return;
      }
      
      setShowDeleteModal(false);
      setItemToDelete(null);
      setShowSuccessPopup(true);
      
      setLocalItems(prevItems => prevItems.filter(item => item.id !== itemToDelete.id));
      
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error('Delete failed with error:', error);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  const completedTable = useMemo(() => {
    return localItems.map((item) => {
      return {
        col1: item.educationName,
        col2: item.educationDetail,
        col3: format(parseISO(item.creation), 'yyyy-MM-dd H:mm'),
        col4: (
          <span className={classNames('inline-flex rounded-full text-xs font-semibold leading-5 px-8 py-1', item.status ? 'bg-green-100' : 'bg-red-100', item.status ? 'text-green-800' : 'text-red-800')}>{item.status ? 'Activo' : 'Inactivo'}</span>
        ),
        col5: (
          <>
            <a href="#" onClick={(e) => _handleClick(e, item)} className="text-dl-primary-900 hover:text-dl-accent mr-3">
              Editar<span className="sr-only">, {item.educationName}</span>
            </a>
            <a href="#" onClick={(e) => handleDelete(e, item)} className="text-red-600 hover:text-red-800">
              Borrar<span className="sr-only">, {item.educationName}</span>
            </a>
          </>
        )
      };
    });
  }, [localItems]);

  const completedTableInstance = useTable(
    { columns: columns, data: completedTable },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!,
    useSortBy,
    usePagination
  );

  const _handleClick = (event, selectedItem) => {
    event.preventDefault();
    onClick && onClick(selectedItem);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-2">
      {/* Error Message Display */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={() => setErrorMessage(null)}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-4 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-6">
            {localItems.length ? <SimpleDataTable tableInstance={completedTableInstance} /> : null}
            {/* <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-[24px]">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gradient-to-r from-blue-700 to-blue-400">
                  <tr>
                    <th scope="col" className="py-3.5 py-5 text-left text-sm font-semibold text-white sm:pl-6">
                      Título
                    </th>
                    <th scope="col" className="px-3 py-5 text-left text-sm font-semibold text-white">
                      Descripción
                    </th>
                    <th scope="col" className="px-3 py-5 text-left text-sm font-semibold text-white">
                      Creación
                    </th>
                    <th scope="col" className="px-3 py-5 text-left text-sm font-semibold text-white">
                      Estado
                    </th>
                    <th scope="col" className="relative px-3 py-5">
                      <span className="sr-only">Editar</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 truncate max-w-xs w-[200px]">
                        <span className="truncate light-bg-sky py-2 px-8 text-black font-normal rounded-full w-32	block text-center">{item.educationName}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-black truncate w-[200px] inline-flex">
                        <span className="truncate">{item.educationDetail}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-black">{format(parseISO(item.creation), 'yyyy-MM-dd H:mm')}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className={classNames('inline-flex rounded-full px-2 text-xs font-semibold leading-5 px-8 py-1', item.status ? 'bg-green-100' : 'bg-red-100', item.status ? 'text-green-800' : 'text-red-800')}>
                          {item.status ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-left text-sm font-medium sm:pr-6">
                        <button onClick={(e) => _handleClick(e, item)} className="text-dl-primary-900 hover:text-dl-accent">
                          Editar<span className="sr-only">, {item.educationName}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div> */}
          </div>
        </div>
      </div>
      
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed top-4 right-4 z-50">
          <SuccessConfirmation 
            message="Educación eliminada exitosamente" 
            onClose={handleCloseSuccessPopup} 
          />
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        title="Confirmar eliminación"
        bodyText={`¿Estás seguro de que quieres eliminar la educación? Esta acción no se puede deshacer.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
